
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"
import { toast } from "sonner"
import { validateCPF, cleanCPF } from "@/utils/cpfValidation"

export type StaffType = "porteiro" | "zelador" | "jardineiro_piscineiro" | "auxiliar_servicos_gerais" | "gerente" | "operador"

export interface StaffMember {
  id: string
  condominium_id: string
  full_name: string
  email: string | null
  phone: string | null
  staff_type: StaffType
  is_active: boolean
  user_profile_id: string | null
  created_at: string
  updated_at: string
  cpf?: string | null
  workload_hours?: number | null
  photo_url?: string | null
  password_hash?: string | null
}

export const useStaffMembers = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["staff-members", profile?.condominium_id],
    queryFn: async (): Promise<StaffMember[]> => {
      if (!profile?.condominium_id) return []

      console.log('Carregando colaboradores para condomínio:', profile.condominium_id)

      const { data, error } = await supabase
        .from("staff_members")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .eq("is_active", true)
        .order("full_name")

      if (error) {
        console.error('Erro ao carregar colaboradores:', error)
        throw error
      }
      
      console.log('Colaboradores carregados:', data?.length)
      // Filter out broken records (no profile linked)
      const valid = (data as StaffMember[]).filter(s => s.user_profile_id !== null)
      return valid
    },
    enabled: !!profile?.condominium_id
  })
}

export const useCreateStaffMember = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: {
      full_name: string
      email?: string
      phone?: string
      staff_type: StaffType
      cpf?: string
      workload_hours?: number
      photo_url?: string
      password?: string
    }) => {
      if (!profile?.condominium_id) {
        throw new Error("Condomínio não identificado")
      }

      console.log('Creating staff member with data:', data)

      // Validar dados obrigatórios
      if (!data.full_name || !data.staff_type) {
        throw new Error('Nome completo e tipo são obrigatórios');
      }

      // Validar CPF apenas se fornecido
      if (data.cpf && !validateCPF(data.cpf)) {
        throw new Error('CPF inválido');
      }

      // Limpar CPF para armazenamento (null se não fornecido)
      const cleanedCPF = data.cpf ? cleanCPF(data.cpf) : null;

      if (cleanedCPF) {
        // Verificar se CPF já existe (apenas ativos)
        const { data: existingStaff, error: checkError } = await supabase
          .from('staff_members')
          .select('cpf, full_name, is_active')
          .eq('cpf', cleanedCPF)
          .eq('is_active', true)
          .maybeSingle();

        if (checkError) {
          console.error('Erro ao verificar CPF existente:', checkError);
          throw new Error('Erro ao verificar CPF: ' + checkError.message);
        }

        if (existingStaff) {
          throw new Error(`Já existe um colaborador ativo com este CPF: ${existingStaff.full_name}`);
        }

        // Verificar se já existe perfil com este CPF
        const { data: existingProfile, error: profileCheckError } = await supabase
          .from('profiles')
          .select('id, cpf')
          .eq('cpf', cleanedCPF)
          .maybeSingle();

        if (profileCheckError) {
          console.error('Erro ao verificar perfil existente:', profileCheckError);
          throw new Error('Erro ao verificar perfil: ' + profileCheckError.message);
        }
      }

      let userProfileId = null;

      // Inserir staff member primeiro com user_profile_id = null
      const { data: staffMember, error: staffError } = await supabase
        .from("staff_members")
        .insert({
          condominium_id: profile.condominium_id,
          full_name: data.full_name,
          email: data.email || (cleanedCPF ? `${cleanedCPF}@colaborador.temp` : `staff-${Date.now()}@colaborador.temp`),
          phone: data.phone || null,
          staff_type: data.staff_type as any,
          cpf: cleanedCPF || null,
          workload_hours: data.workload_hours || 8,
          photo_url: data.photo_url || null,
          user_profile_id: null, // Será definido pela Edge Function
          is_active: true
        } as any)
        .select()
        .single()

      if (staffError) {
        console.error('Error creating staff member:', staffError)
        throw new Error('Erro ao criar colaborador: ' + staffError.message);
      }

      console.log('Staff member created, calling Edge Function for auth setup...')

      // Obter token de acesso da sessão atual
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Usuário não autenticado');
      }

      // Chamar Edge Function para criar/vincular usuário
      const { data: edgeFunctionResult, error: edgeFunctionError } = await supabase.functions.invoke(
        'create-staff-auth-user',
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: {
            staffMemberId: staffMember.id,
            cpf: cleanedCPF,
            fullName: data.full_name,
            email: data.email,
            condominium_id: profile.condominium_id,
            password: data.password
          }
        }
      );

      if (edgeFunctionError) {
        console.error('Erro na Edge Function:', edgeFunctionError);
        // Limpar o staff member criado se a Edge Function falhar
        await supabase.from("staff_members").delete().eq("id", staffMember.id);
        
        // Try to extract detailed error message from Edge Function response
        let errorMessage = 'Erro ao configurar autenticação do colaborador';
        
        try {
          // Check if error has context with response
          if (edgeFunctionError.context?.response) {
            const responseText = await edgeFunctionError.context.response.text();
            const responseJson = JSON.parse(responseText);
            errorMessage = responseJson.error || errorMessage;
          } else if (edgeFunctionError.context?.body) {
            const bodyJson = JSON.parse(edgeFunctionError.context.body);
            errorMessage = bodyJson.error || errorMessage;
          } else if (edgeFunctionError.message) {
            errorMessage = edgeFunctionError.message;
          }
        } catch (parseError) {
          console.error('Could not parse Edge Function error:', parseError);
          errorMessage = edgeFunctionError.message || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      if (!edgeFunctionResult?.success) {
        console.error('Edge Function retornou erro:', edgeFunctionResult);
        // Limpar o staff member criado se a Edge Function falhar
        await supabase.from("staff_members").delete().eq("id", staffMember.id);
        throw new Error(edgeFunctionResult?.error || 'Erro ao configurar autenticação do colaborador');
      }

      console.log('Staff member auth setup completed successfully');

      // Se veio senha temporária, mostrar ao usuário
      if (edgeFunctionResult.temp_password) {
        toast.success(`Colaborador criado! Senha temporária: ${edgeFunctionResult.temp_password}`, {
          duration: 8000
        });
      }

      return { ...staffMember, user_profile_id: edgeFunctionResult.user_id };

    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] })
      toast.success("Colaborador cadastrado com sucesso!")
    },
    onError: (error: any) => {
      console.error("Erro ao cadastrar colaborador:", error)
      const errorMessage = error.message || "Erro ao cadastrar colaborador";
      toast.error(errorMessage)
    }
  })
}

export const useUpdateStaffMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      id: string
      full_name: string
      email?: string
      phone?: string
      staff_type: StaffType
      cpf?: string
      workload_hours?: number
      photo_url?: string
      password?: string
    }) => {
      const { error } = await supabase
        .from("staff_members")
        .update({
          full_name: data.full_name,
          email: data.email || null,
          phone: data.phone || null,
          staff_type: data.staff_type as any,
          cpf: data.cpf ? cleanCPF(data.cpf) : null,
          workload_hours: data.workload_hours,
          photo_url: data.photo_url || null,
          updated_at: new Date().toISOString()
        } as any)
        .eq("id", data.id)

      if (error) throw error

      // Update password via edge function if provided
      if (data.password) {
        const { data: staffData } = await supabase
          .from("staff_members")
          .select("user_profile_id")
          .eq("id", data.id)
          .single()

        if (staffData?.user_profile_id) {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.access_token) {
            const { data: result, error: pwError } = await supabase.functions.invoke('change-user-password', {
              headers: { Authorization: `Bearer ${session.access_token}` },
              body: { userId: staffData.user_profile_id, newPassword: data.password }
            })
            if (pwError || result?.error) {
              console.error("Erro ao atualizar senha:", pwError || result?.error)
              throw new Error(result?.error || "Erro ao atualizar senha")
            }
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] })
      toast.success("Colaborador atualizado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao atualizar colaborador:", error)
      toast.error("Erro ao atualizar colaborador")
    }
  })
}

export const useDeleteStaffMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (staffId: string) => {
      // Arquivar em vez de deletar
      const { error } = await supabase
        .from("staff_members")
        .update({ is_active: false })
        .eq("id", staffId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] })
      queryClient.invalidateQueries({ queryKey: ["archived-staff-members"] })
      toast.success("Colaborador arquivado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao arquivar colaborador:", error)
      toast.error("Erro ao arquivar colaborador")
    }
  })
}

// Hook para buscar colaboradores arquivados
export const useArchivedStaffMembers = () => {
  const { profile } = useAuth()
  
  return useQuery({
    queryKey: ["archived-staff-members", profile?.condominium_id],
    queryFn: async (): Promise<StaffMember[]> => {
      if (!profile?.condominium_id) return []

      console.log('Carregando colaboradores arquivados para condomínio:', profile.condominium_id)

      const { data, error } = await supabase
        .from("staff_members")
        .select("*")
        .eq("condominium_id", profile.condominium_id)
        .eq("is_active", false)
        .order("full_name")

      if (error) {
        console.error('Erro ao carregar colaboradores arquivados:', error)
        throw error
      }
      
      console.log('Colaboradores arquivados carregados:', data?.length)
      const valid = (data as StaffMember[]).filter(s => s.user_profile_id !== null)
      return valid
    },
    enabled: !!profile?.condominium_id
  })
}

// Hook para exclusão permanente
export const useHardDeleteStaffMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (staffId: string) => {
      console.log('Excluindo permanentemente colaborador:', staffId)
      
      const { error } = await supabase.rpc('hard_delete_staff_member', {
        staff_id: staffId
      })

      if (error) {
        console.error('Erro na exclusão permanente:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] })
      queryClient.invalidateQueries({ queryKey: ["archived-staff-members"] })
      toast.success("Colaborador excluído permanentemente!")
    },
    onError: (error) => {
      console.error("Erro ao excluir colaborador permanentemente:", error)
      const msg = error.message || ""
      if (msg.includes("bookings") || msg.includes("foreign key")) {
        toast.error("Não foi possível excluir porque o colaborador possui agendamentos vinculados.")
      } else {
        toast.error("Erro ao excluir colaborador: " + msg)
      }
    }
  })
}

// Hook para restaurar colaborador arquivado
export const useRestoreStaffMember = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (staffId: string) => {
      console.log('Restaurando colaborador:', staffId)
      
      const { error } = await supabase
        .from("staff_members")
        .update({ is_active: true })
        .eq("id", staffId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff-members"] })
      queryClient.invalidateQueries({ queryKey: ["archived-staff-members"] })
      toast.success("Colaborador restaurado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao restaurar colaborador:", error)
      toast.error("Erro ao restaurar colaborador")
    }
  })
}
